import { Component, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { OrderDetailLocationViewModel, OrderHistoryDetailViewModel, paymentMethods } from "../../shared/models";
import { ActivatedRoute } from "@angular/router";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { Location } from "@angular/common";
import { OrderDetailService, paymentMethodsNames } from "./userOrderDetail.service";
import { orderTypes } from "../../shared/models/webshop.model";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { CoreLanguageService } from "../../services/language.service";
import { Subscription } from "rxjs";
import { CoreLogService } from "../../services/log.service";
import Utils from "../../shared/utils";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "userOrderDetail",
  templateUrl: "./userOrderDetail.component.html",
  styleUrls: ["./userOrderDetail.component.scss"],
  providers: [OrderDetailService],
})

export class UserOrderDetailComponent implements OnInit, OnDestroy {
  private orderId: number | string;
  public orderDetailViewModel: OrderHistoryDetailViewModel;
  public orderLocation: OrderDetailLocationViewModel;
  public isNoCOO: boolean;
  public isSmallView: boolean = false;
  private _langSubscriber: Subscription;
  public orderItemsList: string[] = [];
  public image_photo: string;
  public paymentLabel: string = "";

  constructor(private _activatedRoute: ActivatedRoute
    , private _orderDetailService: OrderDetailService
    , private _mainnavService: MainnavService
    , private _location: Location
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _internationalisationService: InternationalisationService
    , private _changeDetectorRef: ChangeDetectorRef) {
    this.isSmallView = this._mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("UserOrderDetailComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me._activatedRoute.paramMap.subscribe(params => {
      me.orderId = params.get("id");
    });
    me.ReloadDataAndRender();
    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });
    me.PushChanges();
  }

  /** Check if there are extra items in the order*/
  public ExtraItems(): boolean {
    const me = this;
    return me.orderDetailViewModel.items.filter(i => i.extras.length > 0).length > 0;
  }

  /**
  * Order type label
  * @param orderType order type number
  * @returns the order type label appended to the value of the order type
  */
  public OrderTypeLabel(orderType: number) {
    const orderTypeMapping = {
      [orderTypes.pickup]: 'phMenuService_TakeAway',
      [orderTypes.delivery]: 'phMenuService_Delivery',
      [orderTypes.deliveryGroup]: 'phMenuService_Delivery_Group',
      [orderTypes.pickupGroup]: 'phMenuService_TakeAway_Group',
      [orderTypes.webvoucher]: 'phMenuService_Webvoucher',
    };
    return this._coreLanguageService.value(orderTypeMapping[orderType] || 'phMenuService_OderToTable');
  }

  /**
   * Payment method name
   * @param valueOperator Payment number
   */
  public PaymentMethodName(valueOperator: number) {
    const me = this;
    const placeholder = paymentMethodsNames[valueOperator];
    if (placeholder) {
      me.paymentLabel = placeholder;
    }
  }

  public isBenefitItem(bonusactionId: number, benefitId: number) {
    const hasBonusActionId = bonusactionId != null && bonusactionId > 0;
    const hasBenefitId = benefitId != null && benefitId > 0;
    return hasBonusActionId && hasBenefitId;
  }

  /**
   * Format currency
   * @param price item price
   * @param hasSymbol currency
   * @param useGrouping group
   * @returns Formated value
   */
  public FormatCurrency(price: number, hasSymbol: boolean = true, useGrouping: boolean = true) {
    return Utils.currencyFormatter(price, this.orderDetailViewModel.currency, hasSymbol, useGrouping);
  }

  /** Check if the order is a group order */
  public isremoteGroupOrder(orderType: number): boolean {
    return orderType === orderTypes.deliveryGroup || orderType === orderTypes.pickupGroup;
  }

  /** check if the value is not null or empty */
  public hasContent(value: string) {
    return value ? !Utils.IsNullOrEmpty(value) : false;
  }

  /** check if there are members */
  public hasMembers() {
    return this.orderDetailViewModel && this.orderDetailViewModel.group_member_names
      && Object.keys(this.orderDetailViewModel.group_member_names).length > 0;
  }

  /** Display the group name */
  public setGroupNameLabel(name: string) {
    return this._coreLanguageService.value("phUserOrderDetail_GroupName")
      .replace("{groupName}", name);
  }

  public get memberEntries(): { id: number; name: string }[] {
    return Object.entries(this.orderDetailViewModel.group_member_names).map(([key, value]) => ({
      id: Number(key),
      name: value
    }));
  }

  public groupMemberItems(cusRefId: number) {
    if (!this.orderDetailViewModel || !this.orderDetailViewModel.items) {
      return [];
    }
    return this.orderDetailViewModel.items.filter(item => item.cusRefId === cusRefId);
  }

  /** Calculate total amount for a member*/
  public calculateTotalMemberAmount(cusRefId: number): string {
    let total = 0;
    if (this.orderDetailViewModel && this.orderDetailViewModel.items && this.orderDetailViewModel.items.length > 0) {
      total = this.orderDetailViewModel.items
        .filter(item => item.cusRefId === cusRefId)
        .reduce((total, item) => total + item.price * item.count, 0);
    }
    return this.FormatCurrency(total);
  }

  /** Check if there is next content in group members*/
  public hasNextContent(i: number) {
    return i !== Object.keys(this.orderDetailViewModel.group_member_names).length - 1;
  }

  /** Reload and render data of order detail*/
  private ReloadDataAndRender(): void {
    const me = this;
    me._orderDetailService.GetOrderDetailViewModel(me.orderId)
      .then(orderDetail => {
        me.orderDetailViewModel = orderDetail;
        me.SetImageString();
        me.PaymentMethodName(me.orderDetailViewModel.payment_method);
      }).catch(e => {
        me._coreLogService.error("UserOrderDetailComponent.ReloadDataAndRender", "GetOrderDetailViewModel", e);
      });
    me.PushChanges();
  }

  /** Set Location image src */
  public SetImageString() {
    const me = this;
    me._orderDetailService.GetOrderLocationViewModel(me.orderDetailViewModel.location).then(location => {
      if (location) {
        me.orderLocation = location;
        me.image_photo = location.location_image;
      }
    });
  }

  /** Navigate to NoCoo */
  public NavigateToNoCoo() {
    const me = this;
    const noCooCode = me.orderDetailViewModel.nocoo_code;
    let nocooURL = `https://nocoo.de/${noCooCode}`;
    if (!environment.production) {
      nocooURL = `https://nocoo.myhypersoft.de/${noCooCode}`;
    }
    window.open(nocooURL);
  }

  /**
   * navigate back via swipe
   */
  public swipeRightHandler() {
    const me = this;
    if (me.isSmallView == true) {
      me._location.back();
    }
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
  }
}

